// Copied verbatim from the desktop app so the two look identical.
// Do not edit here: change the app's copy, then run
//   node scripts/sync-app-components.mjs --write
// `pnpm verify:components` fails the build if the two drift apart.
import { Info } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** Compact "i" affordance that explains a nearby control on hover/focus. */
export function InfoTip({
  label,
  className,
  side = "top"
}: {
  label: string;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
}): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger
        type="button"
        className={cn(
          "inline-flex size-3.5 shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground/80 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50",
          className
        )}
        aria-label={label}
      >
        <Info className="size-3" strokeWidth={2.25} />
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={6} showCaret collisionPadding={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/** Tooltip wrapper for icon buttons / collapsed nav - replaces native `title`. */
export function Tip({
  label,
  children,
  side = "right",
  disabled = false,
  className,
  contentClassName,
  showCaret = false,
  collisionRootRef,
  container,
  portal = true
}: {
  label: ReactNode;
  children: React.ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  disabled?: boolean;
  className?: string;
  /** Classes on the tooltip bubble (default caps width so long copy wraps). */
  contentClassName?: string;
  showCaret?: boolean;
  /** Keep the tip inside this element (e.g. timeline strip) when near edges. */
  collisionRootRef?: RefObject<HTMLElement | null>;
  /** Portal host — use the fullscreen element so tips stay visible there. */
  container?: HTMLElement | null;
  /** When false, skip the body portal (keep under page chrome outside fullscreen). */
  portal?: boolean;
}): React.JSX.Element {
  // Always keep the Tooltip tree mounted. Returning `children` when disabled
  // remounts the trigger on collapse and snaps the sidebar row layout.
  // Stay controlled for the component lifetime - `open={false | undefined}`
  // flips controlled ↔ uncontrolled and triggers a Radix warning.
  const [open, setOpen] = useState(false);
  // After Ctrl+B (or any enable flip) the pointer may still sit on the trigger
  // without a fresh enter - suppress until the pointer leaves the trigger.
  const suppressUntilLeaveRef = useRef(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  // Flipping enabled/disabled always closes the tip. Reset during render so it
  // is shut on the same frame, before the listeners below are (re)attached.
  const [prevDisabled, setPrevDisabled] = useState(disabled);
  if (prevDisabled !== disabled) {
    setPrevDisabled(disabled);
    setOpen(false);
  }

  useEffect(() => {
    if (disabled) {
      suppressUntilLeaveRef.current = false;
      return;
    }

    // `contents` wrapper has no box - resolve the asChild trigger.
    const trigger =
      rootRef.current?.querySelector<HTMLElement>('[data-slot="tooltip-trigger"]') ??
      rootRef.current?.firstElementChild;
    if (!(trigger instanceof HTMLElement)) return;

    suppressUntilLeaveRef.current = true;
    const clear = (): void => {
      suppressUntilLeaveRef.current = false;
      trigger.removeEventListener("pointerleave", clear);
      window.removeEventListener("pointermove", onMove, true);
    };
    const onMove = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && trigger.contains(target)) return;
      clear();
    };
    trigger.addEventListener("pointerleave", clear);
    window.addEventListener("pointermove", onMove, true);
    return clear;
  }, [disabled]);

  return (
    // Default `contents` keeps icon-only tips shrink-wrapped. Pass `className`
    // (e.g. `block w-full`) when the trigger must stretch in a flex column.
    <span ref={rootRef} className={className ?? "contents"}>
      <Tooltip
        open={disabled ? false : open}
        onOpenChange={(next) => {
          if (disabled || (next && suppressUntilLeaveRef.current)) {
            setOpen(false);
            return;
          }
          setOpen(next);
        }}
      >
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={6}
          showCaret={showCaret}
          collisionPadding={8}
          sticky="always"
          // Radix only mounts content once the tip is open, so the caller's
          // boundary element is always mounted by the time this is read. The
          // rule can't prove that ordering; a state copy would just add a
          // render to every consumer for no behavioural gain.
          // eslint-disable-next-line react-hooks/refs
          collisionBoundary={collisionRootRef?.current ?? undefined}
          container={container}
          portal={portal}
          className={contentClassName}
        >
          {label}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
